import CloseIcon from "@mui/icons-material/Close";
import {
  Drawer,
  IconButton,
  Box,
  Typography,
  Stack,
  Divider
} from "@mui/material";
import { formatDateTime } from "@qubit/autoparts";
import JsonView from "@uiw/react-json-view";
import { useTranslation } from "react-i18next";

import { AndonEventWithMeta } from "~/features/andon/andon.types";

export interface EventsDrawerProps {
  open: boolean;
  event: AndonEventWithMeta | null;
  onClose: () => void;
}

export default function EventsDrawer({
  open,
  event,
  onClose
}: EventsDrawerProps) {
  const { t } = useTranslation();

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => {}}
      variant="persistent"
      ModalProps={{
        hideBackdrop: true,
        onBackdropClick: (e) => e.stopPropagation()
      }}
      PaperProps={{
        sx: {
          width: "40vw",
          p: 0,
          boxShadow: "-4px 4px 20px rgba(0, 0, 0, .25)"
        }
      }}
    >
      {/* Box within a box */}
      <Box display="flex" flexDirection="column" height="100%">
        {/* Close Button */}
        <Box display="flex" justifyContent="flex-start" p={1} mt={5}>
          <IconButton aria-label="Close event details" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Scrollable Content */}
        <Box flex={1} overflow="auto" p={2}>
          {!event ? (
            <Typography color="text.disabled">{`${t("no data found")}`}</Typography>
          ) : (
            <Stack>
              {/* Header */}
              <Typography variant="h6">{t("details")}</Typography>

              {/* Event Metadata */}
              <Stack mb={2}>
                <Typography>{`${t("timestamp")}: ${formatDateTime(event.timestamp)}`}</Typography>
                <Typography>{`${t("hub")}: ${event.hub}`}</Typography>
                {event.workstationId && (
                  <Typography>{`${t("workstation")}: ${event.workstationId}`}</Typography>
                )}
                {event.portId && (
                  <Typography>{`${t("port")}: ${event.portId}`}</Typography>
                )}
                {event.gridId && (
                  <Typography>{`Grid: ${event.gridId}`}</Typography>
                )}
                {event.binId && (
                  <Typography>{`Bin: ${event.binId}`}</Typography>
                )}
                {event.status && (
                  <Typography>{`${t("status")}: ${event.status}`}</Typography>
                )}
              </Stack>

              <Divider />

              {/* Event JSON */}
              <Stack mt={2}>
                <Typography variant="subtitle2" mb={1}>
                  Event Data
                </Typography>
                <Box
                  sx={{
                    backgroundColor: "gray.light",
                    p: 1,
                    borderRadius: 1
                  }}
                >
                  <JsonView
                    value={event.event}
                    displayDataTypes={false}
                    collapsed={2}
                    indentWidth={2}
                    style={{
                      fontSize: 14,
                      backgroundColor: "transparent"
                    }}
                    enableClipboard
                  />
                </Box>
              </Stack>
            </Stack>
          )}
        </Box>
      </Box>
    </Drawer>
  );
}
